import { Link } from 'react-router-dom';
import {
  HiShieldCheck,
  HiLockClosed,
  HiUserGroup,
  HiDocumentText,
  HiMail,
} from 'react-icons/hi';
import { trackContactClick } from '../utils/analytics';
import { STORE } from '../utils/store';
import '../styles/Policy.css';

/*
  Privacy Policy. Same shell as the other policies.

  The rights section now points at the pages that actually let someone
  exercise those rights — /data-request and /unsubscribe — and the
  cookies section points at the cookie policy, rather than telling
  people to go and find their browser settings.
*/

const PrivacyPolicy = () => (
  <section className="policy-page">
    <div className="policy-container">
      <header className="policy-head">
        <h1>Privacy Policy</h1>
        <p className="policy-updated">Last updated: 25 September 2026</p>
      </header>

      <div className="policy-body">
        <p className="policy-lead">
          This page explains what Celestial Stones collects when you visit or
          order from us, what it is used for, who else sees it, and how to
          have it corrected or deleted.
        </p>

        <h2>
          <HiDocumentText aria-hidden="true" />
          What We Collect
        </h2>
        <p>To take an order and deliver it safely, we collect:</p>
        <ul className="policy-list">
          <li>Your name, email address, phone number and shipping address</li>
          <li>What you ordered, and the payment reference for it</li>
          <li>
            Device and browser information for analytics, only if you allow
            it on the cookie banner
          </li>
        </ul>

        <h2>
          <HiUserGroup aria-hidden="true" />
          How It Is Used
        </h2>
        <ul className="policy-list">
          <li>To process, pack and deliver your order</li>
          <li>To send order updates and answer support queries</li>
          <li>To understand which stones people look for, so we stock them</li>
          <li>
            To send occasional offers and gemstone recommendations, only with
            your consent
          </li>
          <li>To meet our legal and accounting obligations</li>
        </ul>

        <h2>
          <HiLockClosed aria-hidden="true" />
          How It Is Protected
        </h2>
        <ul className="policy-list">
          <li>The site is served over HTTPS, so everything you send is encrypted in transit</li>
          <li>
            Payment is made by UPI directly to us; no card or bank details pass
            through this website and none are stored
          </li>
          <li>Access to customer records is limited to the people who pack and ship orders</li>
        </ul>

        <h2>Who Else Sees It</h2>
        <p>
          We share the minimum needed with the partners who help fulfil an
          order, and nothing else:
        </p>
        <div className="policy-cards is-three">
          <div className="policy-card">
            <h3>Courier</h3>
            <p>Your name, address and phone number, to deliver the parcel</p>
          </div>
          <div className="policy-card">
            <h3>Payment</h3>
            <p>Your UPI app handles the transaction under its own policy</p>
          </div>
          <div className="policy-card">
            <h3>Analytics</h3>
            <p>Anonymous usage statistics, only with your consent</p>
          </div>
        </div>
        <div className="policy-callout is-good">
          <p>
            <strong>We never sell your personal information</strong> to anyone,
            and we do not share it with advertisers or data brokers.
          </p>
        </div>

        <h2>
          <HiShieldCheck aria-hidden="true" />
          Your Rights
        </h2>
        <p>You can, at any time:</p>
        <ul className="policy-list">
          <li>
            <strong>Access</strong> — ask for a copy of the personal data we hold
          </li>
          <li>
            <strong>Correct</strong> — have anything inaccurate updated
          </li>
          <li>
            <strong>Delete</strong> — have your data removed from our records
          </li>
          <li>
            <strong>Opt out</strong> — stop receiving marketing emails
          </li>
        </ul>
        <p>
          Use the <Link to="/data-request">Data &amp; Privacy Requests</Link>{' '}
          form for access, correction or deletion, and the{' '}
          <Link to="/unsubscribe">unsubscribe page</Link> to leave the mailing
          list. Order records are kept for 7 years for legal and accounting
          purposes even after an account is deleted.
        </p>

        <h2>Cookies</h2>
        <p>
          We set no advertising cookies. The only optional item is Google
          Analytics, which you can allow or decline on the banner and change
          later on the <Link to="/cookie-policy">Cookie Policy</Link> page,
          which lists everything the site stores in your browser.
        </p>

        <h2>Retention</h2>
        <p>
          We keep personal information only as long as needed for the purposes
          above, or as the law requires. Order information is typically kept
          for 7 years.
        </p>

        <h2>
          <HiMail aria-hidden="true" />
          Contact
        </h2>
        <p>Questions or concerns about your privacy:</p>
        <dl className="policy-facts">
          <div className="policy-fact">
            <dt>Email</dt>
            <dd>
              <a
                href={`mailto:${STORE.email}`}
                onClick={() => trackContactClick('email', 'privacy_policy')}
              >
                {STORE.email}
              </a>
            </dd>
          </div>
          <div className="policy-fact">
            <dt>Response</dt>
            <dd>Within 24–48 hours</dd>
          </div>
        </dl>

        <div className="policy-foot">
          <p>
            By using this website you consent to this policy. If it changes,
            the new version will be posted here with an updated date.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default PrivacyPolicy;
